import { Organization } from '../types/organizations';

/**
 * Retrieves the default organization from a list of organizations.
 *
 * @param {Organization[]} organizations - An array of `Organization` objects. Each object may contain a `selected` property to indicate if it is the default organization.
 * @returns {Organization | undefined} - Returns the first organization marked as `selected`, or the first organization in the array if none are marked. Returns `undefined` if the input array is empty or invalid.
 *
 * ### Functionality:
 * - Checks if the input is a valid array and contains at least one organization.
 * - Searches for an organization with the `selected` property set to `true`.
 * - If no organization is marked as `selected`, it defaults to the first organization in the array.
 * - Returns `undefined` if the input is not a valid array or is empty.
 *
 * ### Dependencies:
 * - Relies on the `Organization` type imported from `../types/organizations`.
 *
 * ### Example Usage:
 * ```typescript
 * import { getDefaultOrganization } from './path/to/your/module';
 * import { Organization } from '../types/organizations';
 *
 * const organizations: Organization[] = [
 *   { id: 1, name: 'Org A', selected: false },
 *   { id: 2, name: 'Org B', selected: true },
 *   { id: 3, name: 'Org C', selected: false }
 * ];
 *
 * const defaultOrg = getDefaultOrganization(organizations);
 * console.log(defaultOrg); // Output: { id: 2, name: 'Org B', selected: true }
 * ```
 */
export const getDefaultOrganization = (organizations: Organization[]): Organization | undefined => {
  if (!Array.isArray(organizations) || organizations.length === 0) {
    return undefined;
  }

  const selectedOrg = organizations.find(org => org.selected);
  return selectedOrg ?? organizations[0];
};
